<template>
  <div class="permissions-container">
    <!-- header -->
    <div class="header-area">
      <div class="title-section">
        <h1><i class="fas fa-lock" style="margin-right: 10px;"></i>{{ $t('company.permission.company_permissions_title') }}</h1>
        <div class="company-badge">
          <i class="fas fa-building"></i> {{ $t('company.permission.manage_feature_access') }}:
          <span>{{ companyId }}</span>
        </div>
      </div>
      <button class="save-btn" @click="savePermissions" :disabled="saving">
        <i class="fas" :class="saving ? 'fa-hourglass-half fa-spin' : 'fa-save'"></i>
        {{ saving ? $t('company.permission.saving') : $t('company.permission.save_configuration') }}
      </button>
    </div>

    <!-- loading state -->
    <div v-if="loading" class="loading-area">
      <div class="spinner"></div>
      <p style="margin-top: 1.2rem; font-weight: 500;"><i class="fas fa-database"></i> {{ $t('company.permission.fetching_schema') }}</p>
    </div>

    <!-- Company Status Section -->
    <div v-else class="company-status-section">
      <div class="status-card">
        <div class="status-content">
          <!-- Active Status Toggle -->
          <label class="toggle-switch status-toggle">
            <input type="checkbox" v-model="isActive" @change="onActiveToggle">
            <span class="slider"></span>
            <span class="toggle-label">
              <i class="fas" :class="isActive ? 'fa-check-circle' : 'fa-times-circle'"></i>
              {{ isActive ? $t('company.permission.active') : $t('company.permission.inactive') }}
            </span>
          </label>

          <!-- Store Access Toggle -->
          <label class="toggle-switch status-toggle">
            <input type="checkbox" v-model="allowStoreAccess" @change="onStoreAccessToggle">
            <span class="slider"></span>
            <span class="toggle-label">
              <i class="fas" :class="allowStoreAccess ? 'fa-store' : 'fa-store-slash'"></i>
              {{ $t('company.permission.storeAccessEnabled') }}
            </span>
          </label>

          <!-- Feature Company Toggle -->
          <label class="toggle-switch status-toggle">
            <input type="checkbox" v-model="isFeatureCompany">
            <span class="slider"></span>
            <span class="toggle-label">
              <i class="fas" :class="isFeatureCompany ? 'fa-star' : 'fa-star-half-alt'"></i>
              شرکت برگزیده
            </span>
          </label>

          <!-- Feature Store Toggle -->
          <label class="toggle-switch status-toggle">
            <input type="checkbox" v-model="isFeatureStore">
            <span class="slider"></span>
            <span class="toggle-label">
              <i class="fas" :class="isFeatureStore ? 'fa-star' : 'fa-star-half-alt'"></i>
              فروشگاه برگزیده
            </span>
          </label>

        </div>
      </div>
    </div>

    <!-- roles grid -->
    <div v-if="!loading" class="roles-grid fade-slide">
      <div v-for="(perms, r_id) in namespaces" :key="r_id" class="permission-card">
        <div class="card-header">
          <div class="role-title">
            <span class="role-badge"><i class="fas fa-tag"></i> {{ $t('company.permission.role') }} {{ r_id }}</span>
            <span class="role-name">{{ labels[r_id] || $t('company.permission.role_prefix') + ' ' + r_id }}</span>
          </div>
          <div>
            <i class="fas" :class="getUiState(r_id)?.all ? 'fa-check-circle status-icon' : 'fa-circle status-icon inactive'"></i>
          </div>
        </div>
        <div class="permissions-list">
          <!-- Custom toggle for "Grant Full Access" -->
          <label class="toggle-switch">
            <input type="checkbox" :checked="getUiState(r_id)?.all || false" @change="toggleAll(r_id)">
            <span class="slider"></span>
            <span class="toggle-label"><i class="fas fa-crown"></i> {{ $t('company.permission.grant_full_access') }}</span>
          </label>

          <!-- Individual permissions list -->
          <div :class="{'disabled-overlay': getUiState(r_id)?.all}">
            <div class="features-header">
              <i class="fas fa-list-ul"></i> {{ $t('company.permission.individual_features') }}
            </div>
            <div v-for="(permName, idx) in perms" :key="idx" class="perm-item" @click="toggleCheckbox(r_id, idx)">
              <input 
                type="checkbox" 
                :value="idx + 1"
                :checked="isChecked(r_id, idx)"
                @change="toggleCheckbox(r_id, idx)"
                @click.stop
                :disabled="getUiState(r_id)?.all"
              />
              <span class="perm-name">{{ formatPermissionName(permName) }}</span>
            </div>
            <div v-if="perms.length === 0" class="text-muted" style="font-size:0.85rem; padding: 0.5rem 0;">
              <i class="fas fa-info-circle"></i> {{ $t('company.permission.no_specific_features') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Toast -->
  <transition name="toast">
    <div v-if="showToast" class="toast-notify">
      <div class="toast-icon"><i class="fas fa-check"></i></div>
      <span>{{ $t('company.permission.permissions_updated') }}</span>
    </div>
  </transition>
</template>

<script>
import { adminCompanyService } from '@/services/api';

export default {
  name: 'AdminCompanyPermissions',
  props: {
    companyId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      uiState: {},
      namespaces: {},
      labels: {},
      isActive: true,
      allowStoreAccess: true,
      loading: true,
      saving: false,
      showToast: false,
      isFeatureCompany: false,
      isFeatureStore: false,

    };
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    // Safe getter for uiState
    getUiState(roleId) {
      return this.uiState[roleId] || { all: false, selected: [] };
    },
    
    // Check if a checkbox is checked
    isChecked(roleId, idx) {
      const state = this.getUiState(roleId);
      return state.selected ? state.selected.includes(idx + 1) : false;
    },
    
    async fetchData() {
      this.loading = true;
      try {
        const metaRes = await adminCompanyService.getPermissionMetadata();
        const meta = metaRes.data || metaRes;
        this.namespaces = meta.namespaces || {};
        this.labels = meta.labels || {};

        const stateRes = await adminCompanyService.getCompanyPermissions(this.companyId);
        const state = stateRes.data || stateRes;
        
        // Get Active status from response
        this.isActive = state.is_active !== undefined ? state.is_active : true;
        this.allowStoreAccess = state.allow_store_access !== undefined ? state.allow_store_access : true;

        this.isFeatureCompany = state.is_feature_company !== undefined ? state.is_feature_company : false
        this.isFeatureStore = state.is_feature_store !== undefined ? state.is_feature_store : false

        
        this.uiState = {};
        for (const roleId in this.namespaces) {
          if (state.ui_state && state.ui_state[roleId]) {
            this.uiState[roleId] = {
              all: state.ui_state[roleId].all || false,
              selected: state.ui_state[roleId].selected || []
            };
          } else {
            this.uiState[roleId] = {
              all: false,
              selected: []
            };
          }
        }
      } catch (err) {
        console.error('Failed to load permissions', err);
        this.initializeEmptyState();
      } finally {
        this.loading = false;
      }
    },
    
    initializeEmptyState() {
      this.uiState = {};
      for (const roleId in this.namespaces) {
        this.uiState[roleId] = {
          all: false,
          selected: []
        };
      }
      this.isActive = true;
      this.allowStoreAccess = true;
    },
    
    toggleAll(roleId) {
      if (!this.uiState[roleId]) {
        this.$set(this.uiState, roleId, { all: false, selected: [] });
      }
      this.uiState[roleId].all = !this.uiState[roleId].all;
      if (this.uiState[roleId].all) {
        this.uiState[roleId].selected = [];
      }
    },
    
    onAllToggle(rId) {
      if (this.uiState[rId]?.all) {
        this.uiState[rId].selected = [];
      }
    },
    
    onActiveToggle() {
      console.log('Active status toggled to:', this.isActive);
    },

    onStoreAccessToggle() {
      console.log('Store access toggled to:', this.allowStoreAccess);
    },
    
    toggleCheckbox(rId, idx) {
      // Ensure uiState exists for this role
      if (!this.uiState[rId]) {
        this.$set(this.uiState, rId, { all: false, selected: [] });
      }
      
      if (this.uiState[rId]?.all) return;
      
      const checkboxValue = idx + 1;
      const currentSelected = this.uiState[rId].selected || [];
      const index = currentSelected.indexOf(checkboxValue);
      
      if (index === -1) {
        currentSelected.push(checkboxValue);
      } else {
        currentSelected.splice(index, 1);
      }
      
      this.$set(this.uiState[rId], 'selected', [...currentSelected]);
    },
    
    async savePermissions() {
      this.saving = true;
      try {
        const payload = {
          ui_state: this.uiState,
          is_active: this.isActive,
          allow_store_access: this.allowStoreAccess,
          is_feature_company: this.isFeatureCompany,
          is_feature_store: this.isFeatureStore
        };
        
        await adminCompanyService.updateCompanyPermissions(this.companyId, payload);
        this.triggerToast();
      } catch (err) {
        console.error('Failed to save permissions', err);
        const errorMessage = err.response?.data?.detail || err.message || 'Unknown error';
        alert('Failed to save: ' + errorMessage);
      } finally {
        this.saving = false;
      }
    },
    
    triggerToast() {
      this.showToast = true;
      setTimeout(() => {
        this.showToast = false;
      }, 3000);
    },
    
    formatPermissionName(name) {
      if (!name) return '';
      const formatted = this.$t('permissionKeys.' + name);
      return formatted;
    }
  },
  watch: {
    companyId: {
      async handler(newId, oldId) {
        if (newId !== oldId) {
          await this.fetchData();
        }
      },
      immediate: false
    }
  }
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* main container */
.permissions-container {
  margin: 0 auto;
  background: var(--card-bg);
  backdrop-filter: blur(2px);
  border-radius: 2rem;
  box-shadow: 0 25px 45px -12px var(--shadow-2);
  padding: 1.8rem 2rem 2.5rem;
  transition: all 0.2s ease;
  border: 1px solid var(--card-border);
}

/* header area */
.header-area {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid var(--card-border);
  padding-bottom: 1.5rem;
  margin-bottom: 2rem;
}

.title-section h1 {
  font-size: 1.9rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, var(--heading), var(--muted));
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
}

.title-section h1 i {
  color: var(--primary);
}

.company-badge {
  font-size: 0.9rem;
  color: var(--subtext);
  margin-top: 0.4rem;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.company-badge span {
  background: var(--ring);
  padding: 0.2rem 0.7rem;
  border-radius: 40px;
  font-family: 'Monaco', 'Menlo', monospace;
  font-weight: 600;
  color: var(--primary);
  font-size: 0.85rem;
}

.save-btn {
  background: linear-gradient(105deg, var(--primary) 0%, var(--gradient-1) 100%);
  border: none;
  padding: 0.8rem 1.8rem;
  border-radius: 60px;
  color: white;
  font-weight: 600;
  font-size: 0.95rem;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 8px 18px var(--ring);
  letter-spacing: 0.3px;
}

.save-btn i {
  font-size: 1rem;
  transition: transform 0.2s;
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px var(--ring);
  background: linear-gradient(105deg, var(--primary-hover), var(--primary));
}

.save-btn:active {
  transform: translateY(1px);
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  filter: grayscale(0.1);
  transform: none;
}

/* loading spinner */
.loading-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 0;
}

.loading-area p {
  color: var(--subtext);
}

.spinner {
  width: 52px;
  height: 52px;
  border: 5px solid var(--card-border);
  border-top: 5px solid var(--primary);
  border-radius: 50%;
  animation: spin 0.9s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* roles grid */
.roles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
  gap: 1.8rem;
}

/* permission card */
.permission-card {
  background: var(--card-bg);
  border-radius: 1.75rem;
  box-shadow: 0 8px 20px var(--shadow-1), 0 2px 6px var(--shadow-1);
  transition: all 0.2s;
  border: 1px solid var(--card-border);
  overflow: hidden;
}

.permission-card:hover {
  box-shadow: 0 20px 30px -12px var(--shadow-2), 0 0 0 1px var(--ring);
  transform: translateY(-3px);
}

.card-header {
  background: var(--bg);
  padding: 1.2rem 1.5rem;
  border-bottom: 1px solid var(--card-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
}

.role-title {
  display: flex;
  flex-direction: column;
}

.role-badge {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 700;
  color: var(--primary);
  background: var(--ring);
  display: inline-block;
  padding: 0.2rem 0.65rem;
  border-radius: 50px;
  width: fit-content;
  margin-bottom: 6px;
}

.role-badge i {
  margin-left: 5px;
}

.role-name {
  font-weight: 800;
  font-size: 1.25rem;
  color: var(--heading);
}

.status-icon {
  font-size: 1.4rem;
  color: var(--green);
}

.status-icon.inactive {
  color: var(--muted);
}

/* toggle switch (custom) */
.toggle-switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 1rem;
  margin-top: 0.25rem;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.slider {
  width: 48px;
  height: 26px;
  background-color: var(--muted-2);
  border-radius: 34px;
  transition: 0.2s;
  position: relative;
  cursor: pointer;
  display: inline-block;
}

.slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: 0.2s;
  box-shadow: 0 1px 3px var(--shadow-1);
}

input:checked + .slider {
  background-color: var(--primary);
}

input:checked + .slider:before {
  transform: translateX(22px);
}

.toggle-label {
  margin-left: 12px;
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--heading);
  transition: color 0.2s;
}

.toggle-switch:hover .toggle-label {
  color: var(--primary);
}

/* permissions list */
.permissions-list {
  padding: 1.2rem 1.5rem 1.5rem 1.5rem;
  background: var(--card-bg);
}

.features-header {
  font-size: 0.68rem;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 1px;
  color: var(--muted);
  margin-bottom: 1rem;
  border-top: 1px solid var(--card-border);
  padding-top: 0.8rem;
}

.perm-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.6rem;
  margin: 0 -0.4rem;
  border-radius: 14px;
  transition: background 0.15s;
  cursor: pointer;
}

.perm-item:hover {
  background: var(--ring);
}

.perm-item input {
  width: 18px;
  height: 18px;
  accent-color: var(--primary);
  margin-left: 12px;
  cursor: pointer;
}

.perm-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text);
}

.disabled-overlay {
  opacity: 0.55;
  pointer-events: none;
  filter: grayscale(0.05);
  transition: 0.1s;
}

/* toast message */
.toast-notify {
  position: fixed;
  bottom: 32px;
  right: 32px;
  backdrop-filter: blur(12px);
  background: var(--header-1);
  border-radius: 60px;
  padding: 0.8rem 1.6rem;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
  font-weight: 500;
  box-shadow: 0 20px 35px -12px var(--shadow-3);
  border: 1px solid var(--card-border);
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1);
  pointer-events: none;
}

.toast-icon {
  background: var(--green);
  border-radius: 100%;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
}

.toast-enter-active, .toast-leave-active {
  transition: all 0.35s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateY(40px) scale(0.9);
}

.toast-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

/* utility */
.text-muted {
  color: var(--muted);
}

hr {
  border: none;
  border-top: 1px solid var(--card-border);
  margin: 0.5rem 0;
}

button {
  background: none;
  border: none;
}

@keyframes fadeSlide {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

.fade-slide {
  animation: fadeSlide 0.4s ease-out;
}

/* Responsive */
@media (max-width: 768px) {
  .permissions-container {
    padding: 1.2rem;
  }
  
  .header-area {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
  
  .roles-grid {
    grid-template-columns: 1fr;
  }
  
  .card-header {
    flex-direction: column;
    gap: 0.8rem;
    align-items: flex-start;
  }
}

@media (max-width: 480px) {
  .title-section h1 {
    font-size: 1.5rem;
  }
  
  .save-btn {
    width: 100%;
    justify-content: center;
  }
  
  .perm-item {
    padding: 0.5rem;
  }
  
  .perm-name {
    font-size: 0.85rem;
  }
}
.perm-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.4rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}

.perm-item:hover {
  background: var(--bg);
}

.perm-item input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: var(--primary);
  cursor: pointer;
  flex-shrink: 0;
}

.perm-item .perm-name {
  font-size: 0.9rem;
  color: var(--text);
}

.text-muted {
  color: var(--text-muted);
}
.permissions-container {
  margin: 0 auto;
  background: var(--card-bg);
  backdrop-filter: blur(2px);
  border-radius: 2rem;
  box-shadow: 0 25px 45px -12px var(--shadow-2);
  padding: 1.8rem 2rem 2.5rem;
  transition: all 0.2s ease;
  border: 1px solid var(--card-border);
}

/* Company Status Section */
.company-status-section {
  margin-bottom: 2rem;
}

.status-card {
  background: var(--bg);
  border-radius: 12px;
  padding: 1rem 1.5rem;
  border: 1px solid var(--card-border);
}

.status-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.status-toggle {
  margin: 0;
}

.status-toggle .toggle-label {
  font-weight: 500;
  font-size: 1rem;
}

.status-toggle .toggle-label .fa-check-circle {
  color: #22c55e;
}

.status-toggle .toggle-label .fa-times-circle {
  color: #ef4444;
}

/* Disabled overlay */
.disabled-overlay {
  opacity: 0.5;
  pointer-events: none;
}

/* Responsive */
@media (max-width: 768px) {
  .permissions-container {
    padding: 1rem;
  }
  
  .header-area {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
  
  .save-btn {
    width: 100%;
    justify-content: center;
  }
  
  .roles-grid {
    grid-template-columns: 1fr;
  }
}

.status-content {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: center;
}

.status-toggle .toggle-label .fa-store {
  color: #22c55e;
}

.status-toggle .toggle-label .fa-store-slash {
  color: #ef4444;
}
</style>